import { flattenVocabularies, renderView } from '@clawharbor/render-core';
import type { Row } from '@clawharbor/render-core';
import { html } from './html.js';
import type { VNode } from './html.js';
import type { AppState, ResultRecord } from './store.js';
import { Controls } from './ui/controls.js';
import type { FormValues } from './ui/controls.js';
import { DebugStrip, DisclaimerBar, Footer, Masthead, StagedPanel } from './ui/chrome.js';

/**
 * The whole page, as a pure function of store state.
 *
 * Pure on purpose: an agent's `execute()` writes to the store and the page
 * repaints with no other coordination. If the agent path could reach the DOM any
 * other way, the two halves of this surface would be able to disagree — and on
 * WebMCP, where the page is the tool's ONLY UI, that disagreement is the whole
 * failure mode.
 */

export interface AppActions {
  selectCapability(id: string): void;
  setField(capabilityId: string, name: string, value: unknown): void;
  submit(capabilityId: string): void;
  reset(capabilityId: string): void;
  /** Human picks a row: stages it locally, no network, no purchase. */
  selectRow(capabilityId: string, row: Row): void;
  clearStaged(): void;
  retryBoot(): void;
}

export interface AppProps {
  state: AppState;
  forms: Record<string, FormValues>;
  actions: AppActions;
  now: Date;
}

function Booting(): VNode {
  return html`<div class="chw-shell ch-root"><p class="ch-empty">Loading…</p></div>`;
}

function BootError(message: string, retry: () => void): VNode {
  return html`
    <div class="chw-shell ch-root">
      <div class="chw-error">
        <strong>This app could not load its manifest.</strong>
        <div class="chw-hint">${message}</div>
      </div>
      <button class="ch-btn" type="button" onClick=${retry}>Try again</button>
    </div>
  `;
}

function Results(props: AppProps, record: ResultRecord | undefined): VNode | null {
  const { state, actions } = props;
  const capability = state.manifest?.capabilities.find((c) => c.id === state.activeCapability);
  const manifest = state.manifest;
  if (!capability || !manifest) return null;

  const view = manifest.views.find((v) => v.id === capability.view);
  if (!view) {
    return html`<div class="chw-error">
      View <code>${capability.view}</code> is missing from the manifest.
    </div>`;
  }

  // Site jargon neither the reader nor the agent can be expected to know
  // (TITL -> Subtitled), decoded from the manifest's own glossary.
  const labels = flattenVocabularies(manifest.domain.vocabularies, manifest.identity.locale);

  if (!record) {
    return html`
      <section class="ch-root">
        ${renderView(view, [], {
          brand: manifest.brand,
          policy: manifest.policy,
          origin: manifest.source.canonicalOrigin,
          emptyText: view.emptyText ?? 'Run a search above — or ask your agent to.',
          locale: manifest.identity.locale,
          labels,
        })}
      </section>
    `;
  }

  return html`
    <section class="ch-root">
      <div class="chw-results-head">
        <span class="ch-chip" title="which side triggered this result">
          ${record.origin === 'agent' ? 'from your agent' : 'from this page'} · ${record.durationMs}ms
        </span>
      </div>
      <p class="chw-panel-desc">${record.summary}</p>
      ${renderView(view, record.result.rows, {
        brand: manifest.brand,
        policy: manifest.policy,
        freshness: record.result.freshness,
        origin: manifest.source.canonicalOrigin,
        locale: manifest.identity.locale,
        labels,
        showContrastWarnings: false,
        // Selecting a row repaints the live page rather than returning UI —
        // which is the only thing WebMCP lets a tool do, and the point of it.
        onSelect: (row) => actions.selectRow(capability.id, row),
        selectLabel: capability.kind === 'stage' ? 'Stage' : 'Choose',
      })}
      ${record.result.notes.length
        ? html`<p class="chw-hint">${record.result.notes.join(' · ')}</p>`
        : null}
      ${record.deepLink
        ? html`<div class="chw-actions">
            <a class="ch-btn" href=${record.deepLink} target="_blank" rel="noopener noreferrer">
              Continue on the official site ↗
            </a>
          </div>`
        : null}
      <p class="chw-hint">
        Showing data as read at
        ${record.result.freshness.fetchedAt ?? new Date(record.receivedAt).toISOString()}. Every
        booking is completed on the operator's own site.
      </p>
    </section>
  `;
}

export function AppView(props: AppProps): VNode {
  const { state, actions, now } = props;

  if (state.status === 'booting') return Booting();
  if (state.status === 'error' || !state.manifest) {
    return BootError(state.error ?? 'unknown error', actions.retryBoot);
  }

  const manifest = state.manifest;
  const capabilities = manifest.capabilities.filter((c) => c.surfaces.web.register);
  const active =
    capabilities.find((c) => c.id === state.activeCapability) ??
    capabilities[0] ??
    manifest.capabilities[0];

  const record = active ? state.results[active.view] : undefined;

  return html`
    <div>
      ${DisclaimerBar(manifest)}
      <div class="chw-shell ch-root">
        ${Masthead(manifest, record?.result.freshness, now)}
        ${DebugStrip(state.webmcp, state.toolLog)}
        ${StagedPanel(state.staged, actions.clearStaged)}

        <nav class="chw-tabs" role="tablist" aria-label="Actions">
          ${capabilities.map(
            (cap) => html`
              <button
                key=${cap.id}
                class="chw-tab"
                role="tab"
                type="button"
                aria-selected=${cap.id === active?.id ? 'true' : 'false'}
                onClick=${() => actions.selectCapability(cap.id)}
              >
                ${cap.title}
              </button>
            `,
          )}
        </nav>

        ${active
          ? Controls({
              capability: active,
              values: props.forms[active.id] ?? {},
              pending: state.pending[active.id] === true,
              onChange: (name, value) => actions.setField(active.id, name, value),
              onSubmit: () => actions.submit(active.id),
              onReset: () => actions.reset(active.id),
            })
          : null}

        ${Results(props, record)} ${Footer(manifest)}
      </div>
    </div>
  `;
}
